import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { StoreBackstockLedgerDayModel } from "models";
import { auditDashboardTheme } from "../../theme";
import { buildAuditDashboardCurrentInventoryStatusPath, AuditDashboardInventoryMetric } from "../../utils/currentInventoryStatusPath";

interface AuditDetailInventorySectionProps {
  backstockLedgerDay: StoreBackstockLedgerDayModel | null;
  reportDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isDayInProgress?: boolean;
  hasSubmittedAudits?: boolean;
}

type InventoryValueMode = "plain" | "signed" | "negative";

interface InventoryCellConfig {
  qty: number;
  packs: number;
  metric: AuditDashboardInventoryMetric;
  packTitle: string;
  mode?: InventoryValueMode;
}

interface InventoryTableRow {
  label: "Backstock" | "Vending" | "Counter";
  opening: InventoryCellConfig;
  activated: InventoryCellConfig;
  moved: InventoryCellConfig;
  soldOut: InventoryCellConfig;
  closing: InventoryCellConfig;
}

interface InventoryValueCellProps {
  value: number;
  mode?: InventoryValueMode;
}

interface InventoryPackCellProps extends InventoryValueCellProps {
  packTitle: string;
  to?: string | null;
}

const inventoryGroups = [
  { label: "Opening", key: "opening" },
  { label: "Activated", key: "activated" },
  { label: "Moved", key: "moved" },
  { label: "Sold Out", key: "soldOut" },
  { label: "Closing", key: "closing" },
] as const;

const inventoryGridColumns = "minmax(120px, 0.82fr) repeat(5, minmax(42px, 0.4fr) minmax(42px, 0.4fr))";

const toNumber = (value: number | null | undefined): number => Number(value ?? 0);

const formatTableValue = (value: number, mode: InventoryValueMode = "plain") => {
  if (value === 0) {
    return "-";
  }

  const absoluteValue = Math.abs(value).toLocaleString();

  if (mode === "signed") {
    return `${value > 0 ? "+" : "-"}${absoluteValue}`;
  }

  if (mode === "negative") {
    return `-${absoluteValue}`;
  }

  return absoluteValue;
};

const getMovementMetric = (
  value: number,
  positiveMetric: AuditDashboardInventoryMetric,
  negativeMetric: AuditDashboardInventoryMetric
) => (value >= 0 ? positiveMetric : negativeMetric);

const getMovementDirection = (packs: number, tickets: number) => (packs !== 0 ? packs : tickets);

const getInventoryGroupGapSx = (groupIndex: number) =>
  ({
    ml: "34px",
    pl: "34px",
  });

function InventoryValueCell({ value, mode = "plain" }: InventoryValueCellProps) {
  return (
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, lineHeight: 1.2, color: "inherit" }}>
      {formatTableValue(value, mode)}
    </Typography>
  );
}

function InventoryPackCell({ value, mode = "plain", packTitle, to }: InventoryPackCellProps) {
  const isClickable = Boolean(to) && value !== 0;
  const displayValue = formatTableValue(value, mode);

  if (!isClickable) {
    return <InventoryValueCell value={value} mode={mode} />;
  }

  return (
    <Box
      component={Link}
      to={to || ""}
      title={packTitle}
      sx={{
        appearance: "none",
        border: 0,
        bgcolor: "transparent",
        p: 0,
        m: 0,
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.2,
        color: auditDashboardTheme.colors.blue,
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "center",
        textDecoration: "underline",
        textUnderlineOffset: "2px",
        "&:hover": {
          color: auditDashboardTheme.colors.blue,
          textDecoration: "underline",
        },
        "&:focus-visible": {
          outline: `2px solid ${auditDashboardTheme.colors.blue}`,
          outlineOffset: "2px",
          borderRadius: "2px",
        },
      }}
    >
      {displayValue}
    </Box>
  );
}

export default function AuditDetailInventorySection({
  backstockLedgerDay,
  reportDate,
  startDate,
  endDate,
  isDayInProgress = false,
  hasSubmittedAudits = false,
}: AuditDetailInventorySectionProps) {
  const { id: storeId } = useParams<{ id: string }>();
  const resolvedReportDate = backstockLedgerDay?.reportDate || reportDate || undefined;
  const resolvedStartDate = startDate || undefined;
  const resolvedEndDate = endDate || undefined;
  const canOpenPackScope = Boolean(
    storeId && !isDayInProgress && (resolvedReportDate || (resolvedStartDate && resolvedEndDate))
  );

  const rows = useMemo<InventoryTableRow[]>(() => {
    if (!backstockLedgerDay && !isDayInProgress) {
      return [];
    }

    const shouldHideInventoryValues = isDayInProgress && !hasSubmittedAudits;
    const getValue = (value: number | null | undefined) => (shouldHideInventoryValues ? 0 : toNumber(value));

    const counterToBackstockTickets = getValue(backstockLedgerDay?.counterToBackstockTickets);
    const counterToBackstockPacks = getValue(backstockLedgerDay?.counterToBackstockPacks);
    const counterFromBackstockTickets = getValue(backstockLedgerDay?.counterFromBackstockTickets);
    const counterFromBackstockPacks = getValue(backstockLedgerDay?.counterFromBackstockPacks);
    const returnedToLotteryTickets = getValue(backstockLedgerDay?.returnedToLotteryTickets);
    const returnedToLotteryPacks = getValue(backstockLedgerDay?.returnedToLotteryPacks);

    const backstockMovedTickets = counterToBackstockTickets - counterFromBackstockTickets - returnedToLotteryTickets;
    const backstockMovedPacks = counterToBackstockPacks - counterFromBackstockPacks - returnedToLotteryPacks;
    const counterMovedTickets = counterFromBackstockTickets - counterToBackstockTickets;
    const counterMovedPacks = counterFromBackstockPacks - counterToBackstockPacks;

    return [
      {
        label: "Backstock",
        opening: {
          qty: getValue(backstockLedgerDay?.openingTickets),
          packs: getValue(backstockLedgerDay?.openingPacks),
          metric: "backstock_opening",
          packTitle: "Open these backstock opening packs in current inventory",
        },
        activated: {
          qty: getValue(backstockLedgerDay?.activatedToBackstockTickets),
          packs: getValue(backstockLedgerDay?.activatedToBackstockPacks),
          metric: "backstock_activated_to_backstock",
          packTitle: "Open these activation-to-backstock packs in current inventory",
          mode: "signed",
        },
        moved: {
          qty: backstockMovedTickets,
          packs: backstockMovedPacks,
          metric: getMovementMetric(
            getMovementDirection(backstockMovedPacks, backstockMovedTickets),
            "backstock_moved_from_counter",
            "backstock_returned_to_counter"
          ),
          packTitle: "Open these moved backstock packs in current inventory",
          mode: "signed",
        },
        soldOut: {
          qty: getValue(backstockLedgerDay?.inventoryDepletionTickets),
          packs: getValue(backstockLedgerDay?.inventoryDepletionPacks),
          metric: "backstock_inventory_depletion",
          packTitle: "Open these sold-out backstock packs in current inventory",
          mode: "negative",
        },
        closing: {
          qty: getValue(backstockLedgerDay?.closingTickets),
          packs: getValue(backstockLedgerDay?.closingPacks),
          metric: "backstock_closing",
          packTitle: "Open these backstock closing packs in current inventory",
        },
      },
      {
        label: "Counter",
        opening: {
          qty: getValue(backstockLedgerDay?.counterOpeningTickets),
          packs: getValue(backstockLedgerDay?.counterOpeningPacks),
          metric: "opening",
          packTitle: "Open these opening packs in current inventory",
        },
        activated: {
          qty: getValue(backstockLedgerDay?.counterActivatedTickets),
          packs: getValue(backstockLedgerDay?.counterActivatedPacks),
          metric: "activated",
          packTitle: "Open these activated packs in current inventory",
          mode: "signed",
        },
        moved: {
          qty: counterMovedTickets,
          packs: counterMovedPacks,
          metric: getMovementMetric(
            getMovementDirection(counterMovedPacks, counterMovedTickets),
            "backstock_returned_to_counter",
            "moved_to_backstock"
          ),
          packTitle: "Open these moved counter packs in current inventory",
          mode: "signed",
        },
        soldOut: {
          qty: getValue(backstockLedgerDay?.counterSoldOutTickets),
          packs: getValue(backstockLedgerDay?.counterSoldOutPacks),
          metric: "counter_sold_out",
          packTitle: "Open these counter sold-out packs in current inventory",
          mode: "negative",
        },
        closing: {
          qty: getValue(backstockLedgerDay?.counterClosingTickets),
          packs: getValue(backstockLedgerDay?.counterClosingPacks),
          metric: "closing",
          packTitle: "Open these closing packs in current inventory",
        },
      },
      ...(backstockLedgerDay?.hasVendingInventory
        ? [
            {
              label: "Vending",
              opening: {
                qty: getValue(backstockLedgerDay.vendingOpeningTickets),
                packs: getValue(backstockLedgerDay.vendingOpeningPacks),
                metric: "vending_opening",
                packTitle: "Open these vending opening packs in current inventory",
              },
              activated: {
                qty: getValue(backstockLedgerDay.vendingActivatedTickets),
                packs: getValue(backstockLedgerDay.vendingActivatedPacks),
                metric: "vending_activated",
                packTitle: "Open these vending activated packs in current inventory",
                mode: "signed",
              },
              moved: {
                qty: getValue(backstockLedgerDay.vendingFromBackstockTickets) - getValue(backstockLedgerDay.vendingToBackstockTickets),
                packs: getValue(backstockLedgerDay.vendingFromBackstockPacks) - getValue(backstockLedgerDay.vendingToBackstockPacks),
                metric: "vending_moved",
                packTitle: "Open these vending moved packs in current inventory",
                mode: "signed",
              },
              soldOut: {
                qty: getValue(backstockLedgerDay.vendingSoldOutTickets),
                packs: getValue(backstockLedgerDay.vendingSoldOutPacks),
                metric: "vending_sold_out",
                packTitle: "Open these vending sold-out packs in current inventory",
                mode: "negative",
              },
              closing: {
                qty: getValue(backstockLedgerDay.vendingClosingTickets),
                packs: getValue(backstockLedgerDay.vendingClosingPacks),
                metric: "vending_closing",
                packTitle: "Open these vending closing packs in current inventory",
              },
            } satisfies InventoryTableRow,
          ]
        : []),
    ];
  }, [backstockLedgerDay, hasSubmittedAudits, isDayInProgress]);

  const getPackScopePath = (metric: AuditDashboardInventoryMetric) => {
    if (!storeId || !canOpenPackScope) {
      return null;
    }

    return buildAuditDashboardCurrentInventoryStatusPath(storeId, {
      metric,
      reportDate: resolvedReportDate,
      startDate: resolvedStartDate,
      endDate: resolvedEndDate,
    });
  };

  if (rows.length === 0 || !storeId || (!resolvedReportDate && !(resolvedStartDate && resolvedEndDate))) {
    return null;
  }

  return (
    <Box>
      <Typography component="div" sx={{ fontSize: 18, fontWeight: 700, color: auditDashboardTheme.colors.text, lineHeight: 1.2 }}>
        Inventory
      </Typography>

      <Box
        sx={{
          mt: 1.5,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(163, 174, 208, 0.42) transparent",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(163, 174, 208, 0.42)", borderRadius: 999 },
        }}
      >
        <Box
          sx={{
            minWidth: 760,
            border: "1px solid",
            borderColor: "#0000001f",
            borderRadius: "8px",
            overflow: "hidden",
            bgcolor: auditDashboardTheme.colors.surface,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: inventoryGridColumns,
              alignItems: "end",
              borderBottom: `1px solid ${auditDashboardTheme.colors.lineSoft}`,
              bgcolor: "#f0f2f2",
              pb: "4px",
            }}
          >
            <Typography
              component="div"
              sx={{
                gridColumn: "1",
                gridRow: "1 / span 2",
                px: "10px",
                py: "4px",
                color: auditDashboardTheme.colors.text,
                fontSize: 12,
                fontWeight: 800,
                lineHeight: 1.2,
                textAlign: "left",
                textTransform: "uppercase",
              }}
            >
              Location
            </Typography>
            {inventoryGroups.map((group, index) => {
              const columnStart = 2 + index * 2;

              return (
                <Typography
                  key={group.key}
                  component="div"
                  sx={{
                    gridColumn: `${columnStart} / span 2`,
                    gridRow: "1",
                    px: "6px",
                    pr: index === inventoryGroups.length - 1 ? "10px" : "6px",
                    py: "4px",
                    ...getInventoryGroupGapSx(index),
                    color: auditDashboardTheme.colors.text,
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    textAlign: "right",
                    textTransform: "uppercase",
                  }}
                >
                  {group.label}
                </Typography>
              );
            })}
            {inventoryGroups.flatMap((group, index) => {
              const columnStart = 2 + index * 2;

              return [
                <Typography
                  key={`${group.key}-qty`}
                  component="div"
                  sx={{
                    gridColumn: `${columnStart}`,
                    gridRow: "2",
                    px: "4px",
                    py: "2px",
                    ...getInventoryGroupGapSx(index),
                    color: auditDashboardTheme.colors.textMutedStrong,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.2,
                    textAlign: "right",
                  }}
                >
                  qty
                </Typography>,
                <Typography
                  key={`${group.key}-pk`}
                  component="div"
                  sx={{
                    gridColumn: `${columnStart + 1}`,
                    gridRow: "2",
                    px: "4px",
                    pr: index === inventoryGroups.length - 1 ? "10px" : "4px",
                    py: "2px",
                    color: auditDashboardTheme.colors.textMutedStrong,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.2,
                    textAlign: "right",
                  }}
                >
                  pk
                </Typography>,
              ];
            })}
          </Box>

          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                display: "grid",
                gridTemplateColumns: inventoryGridColumns,
                alignItems: "center",
                borderBottom: `1px solid ${auditDashboardTheme.colors.lineSoft}`,
                bgcolor: auditDashboardTheme.colors.surface,
              }}
            >
              <Typography
                component="div"
                sx={{
                  px: "10px",
                  py: "8px",
                  color: auditDashboardTheme.colors.text,
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  textAlign: "left",
                }}
              >
                {row.label}
              </Typography>
              {inventoryGroups.flatMap((group, index) => {
                const cell = row[group.key];
                const columnStart = 2 + index * 2;

                return [
                  <Box
                    key={`${row.label}-${group.key}-qty`}
                    sx={{
                      gridColumn: `${columnStart}`,
                      px: "4px",
                      py: "8px",
                      ...getInventoryGroupGapSx(index),
                      color: auditDashboardTheme.colors.text,
                      textAlign: "right",
                    }}
                  >
                    <InventoryValueCell value={cell.qty} mode={cell.mode} />
                  </Box>,
                  <Box
                    key={`${row.label}-${group.key}-packs`}
                    sx={{
                      gridColumn: `${columnStart + 1}`,
                      px: "4px",
                      pr: index === inventoryGroups.length - 1 ? "10px" : "4px",
                      py: "8px",
                      color: auditDashboardTheme.colors.text,
                      textAlign: "right",
                    }}
                  >
                    <InventoryPackCell
                      value={cell.packs}
                      mode={cell.mode}
                      packTitle={cell.packTitle}
                      to={getPackScopePath(cell.metric)}
                    />
                  </Box>,
                ];
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
